<template>
  <b-form @submit.stop.prevent="handleSubmit">
    <b-row>
      <b-col cols="12">
        <b-form-group
          :label="fieldLabels.name"
          label-for="commission-plan-name"
        >
          <b-form-input
            name="commission-plan-name"
            v-model="$v.form.name.$model"
            :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            type="text"
          />
          <b-form-invalid-feedback
            v-if="$v.form.name.$dirty"
            class="commission-plan-name-feedback"
          >
            <span v-if="!$v.form.name.required">Please enter a name.</span>
            <span v-if="!$v.form.name.maxLength">
              The name must be less than {{ $v.form.name.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.name.serverFailed">{{ serverErrors.name }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-if="newForm"
        cols="12"
      >
        <b-form-group
          :label="fieldLabels.user_id"
          label-for="commission-plan-user_id"
        >
          <employee-select
            name="commission-plan-user_id"
            :select-class="{ 'is-invalid': $v.form.user_id.$dirty && $v.form.user_id.$error }"
            :value="form.user"
            @input="onUserChange"
          />
          <b-form-invalid-feedback
            v-if="$v.form.user_id.$dirty"
            class="commission-plan-user_id-feedback"
          >
            <span v-if="!$v.form.user_id.required">Please enter a user.</span>
            <span v-if="!$v.form.user_id.serverFailed">
              {{ serverErrors.user_id }}
            </span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="newForm">
      <div
        v-for="(v, index) in $v.form.rates_by_year.$each.$iter"
        :key="index"
      >
        <b-row>
          <b-col
            v-if="newForm"
            cols="12"
          >
            <b-form-group
              label-for="commission-plan-rate_percent"
            >
              <template #label>
                <b-row>
                  <b-col>
                    {{ `Year ${parseFloat(index) + 1} Rate` }}
                  </b-col>
                  <b-col cols="auto">
                    <b-button
                      v-if="index != 0"
                      class="font-size-lg py-0 ml-auto"
                      v-b-tooltip.hover
                      title="Remove the Rate"
                      variant="link"
                      @click="() => removeRate(index)"
                    >
                      <i class="fa fa-close" />
                    </b-button>
                  </b-col>
                </b-row>
              </template>
              <b-input-group
                append="%"
                :class="{ 'is-invalid': v.rate_percent.$dirty && v.rate_percent.$error }"
              >
                <b-form-input
                  name="commission-plan-rate_percent"
                  :value="v.rate_percent.$model"
                  @change="(newRate) => onRateChange(index, newRate)"
                  :state="v.rate_percent.$dirty ? !v.rate_percent.$error : null"
                  type="text"
                />
              </b-input-group>
              <b-form-invalid-feedback
                v-if="v.rate_percent.$dirty"
                class="commission-plan-rate_percent-feedback"
              >
                <span v-if="!v.rate_percent.integer">Please enter a number.</span>
                <span v-if="!v.rate_percent.between">Must be between {{ v.rate_percent.$params.between.min }} and {{ v.rate_percent.$params.between.max }}</span>
                <span v-if="!v.rate_percent.serverFailed">{{ serverErrors.rate }}</span>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-row v-if="newForm">
      <b-col>
        <b-button
          id="add-charge-button"
          class="mt-1"
          variant="outline-secondary"
          @click="addRate"
        >
          <i class="fa fa-plus mr-1" />Add Rate
        </b-button>
      </b-col>
    </b-row>

    <hr class="mt-4">

    <b-row>
      <b-col
        offset-lg="6"
        lg="3"
        order="2"
        order-lg="1"
      >
        <b-button
          variant="link-dark"
          block
          class="commission-plan-close-button"
          @click="$emit('on-cancel')"
        >
          Cancel
        </b-button>
      </b-col>
      <b-col
        lg="3"
        order="1"
        order-lg="2"
      >
        <b-button
          class="commission-plan-submit-button"
          block
          type="submit"
          variant="primary"
          :disabled="processing"
        >
          Save
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { required, maxLength, requiredIf, decimal, between } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import EmployeeSelect from '@/components/admin/users/EmployeeSelect.vue';

export default {
  name: 'CommissionPlansForm',
  components: {
    EmployeeSelect,
  },
  mixins: [formMixin],
  props: {
    name: String,
    user_id: Number,
    user: Object,
    newForm: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {
        name: this.name,
        rates_by_year: [{
          rate: null,
          rate_percent: null,
        }],
        user_id: this.user_id,
        user: this.user,
      },
      fieldLabels: {
        name: 'Name',
        rate_percent: 'Commission Plan',
        user_id: 'User',
      },
    };
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('name');
        },
      },
      user_id: {
        required: requiredIf(function () {
          return this.newForm;
        }),
        serverFailed() {
          return !this.hasServerErrors('user_id');
        },
      },
      rates_by_year: {
        required: requiredIf(function () {
          return this.newForm;
        }),
        $each: {
          rate_percent: {
              required: requiredIf(function () {
                return this.newForm;
              }),
            decimal,
            between: between(0, 100),
            serverFailed() {
              return !this.hasServerErrors('rate');
            },
          },
        }
      },
    },
  },
  methods: {
    onUserChange(user) {
      this.form.user_id = user ? user.id : null;
      this.form.user = user;
    },
    onRateChange(index, newRatePercent) {
      this.form.rates_by_year[index].rate_percent = newRatePercent;
      this.form.rates_by_year[index].rate = isNaN(newRatePercent) ? null : newRatePercent / 100.0;
    },
    addRate() {
      this.form.rates_by_year.push({
        rate: null,
        rate_percent: null,
      });
    },
    removeRate(index) {
      this.form.rates_by_year.splice(index, 1);
    },
  },
};
</script>
